import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { loginSchema, type LoginInput } from '@oficina/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/common/field';
import { BrandMark } from '@/layouts/sidebar';
import { useAuth } from '@/hooks/use-auth';
import { FullScreenLoader } from '@/routes/guards';
import { errorMessage } from '@/lib/api';

/** Tela de login (mesmo visual do protótipo), agora autenticando na API — sem credenciais no código. */
export function LoginPage() {
  const { user, isLoading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/';
  const [error, setError] = useState<string | null>(null);
  const form = useForm<LoginInput>({ resolver: zodResolver(loginSchema), defaultValues: { email: '', password: '' } });
  const { register, handleSubmit, formState } = form;

  if (isLoading) return <FullScreenLoader />;
  if (user) return <Navigate to={from} replace />;

  const onSubmit = handleSubmit(async (values) => {
    setError(null);
    try {
      await login(values);
      navigate(from, { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    }
  });

  return (
    <div className="grid min-h-dvh place-items-center bg-[#f4f7f5] p-5">
      <div className="w-full max-w-[410px] rounded-2xl border bg-white p-8 shadow-[0_20px_65px_#142c2210] sm:p-9">
        <div className="mb-7 flex justify-center">
          <BrandMark stacked />
        </div>
        <h1 className="mb-1.5 text-center font-display text-[23px] font-extrabold">Bem-vindo de volta</h1>
        <p className="mb-7 text-center text-muted-foreground">Acesse o FixTech com seu e-mail e senha</p>
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <Field label="E-mail" htmlFor="email" error={formState.errors.email?.message}>
            <Input id="email" type="email" autoComplete="username" autoFocus aria-invalid={!!formState.errors.email} {...register('email')} />
          </Field>
          <Field label="Senha" htmlFor="password" error={formState.errors.password?.message}>
            <Input id="password" type="password" autoComplete="current-password" aria-invalid={!!formState.errors.password} {...register('password')} />
          </Field>
          {error && (
            <p role="alert" className="rounded-lg bg-[#fff0ee] px-3 py-2 text-[13px] font-medium text-destructive">
              {error}
            </p>
          )}
          <Button type="submit" className="mt-2 h-11 w-full" loading={formState.isSubmitting}>
            Entrar
          </Button>
        </form>
        <p className="mt-5 text-center text-[11px] text-[#879397]">Acesso restrito à equipe da oficina.</p>
      </div>
    </div>
  );
}
